import { Component, Input, OnInit, OnDestroy, ViewChild, NgZone, ElementRef, Output, EventEmitter } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { convert } from 'html-to-text';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil, take } from 'rxjs/operators';
import {
  DateTimeCellComponent,
  GeometryCellComponent,
  TextCellComponent,
  AutoIncrementCellComponent,
  UserCellComponent,
  NumberCellComponent,
  UrlCellComponent,
  PicklistCellComponent,
  MultiPicklistCellComponent,
  ImageCellComponent,
  WorkflowCellComponent,
  BooleanCellComponent,
  ColorCellComponent,
  DistanceCellComponent,
  CultureCodeCellComponent,
  DurationCellComponent,
  EnumerationCellComponent,
  BusinessUnitsCellComponent,
  ReferenceCellComponent
} from 'src/app/modules/core/components/ag-grid';
import { Document, DocumentSchema, SchemaField } from 'src/app/modules/core/store/models/document.model';
import {
  AgGridEvent,
  GridApi,
  RowNode,
  RowClassParams,
  GetRowIdParams,
  GetLocaleTextParams,
  PaginationChangedEvent,
  RowSelectionOptions,
  ColDef
} from 'ag-grid-enterprise';
import * as XLSX from 'xlsx';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { DocumentReader } from 'src/app/modules/core/store/models';
import { LinkCellComponent } from 'src/app/modules/core/components/ag-grid/link-cell/link-cell.component';
import { ActionCellComponent } from 'src/app/modules/core/components/ag-grid/action-cell/action-cell.component';
import {
  TableStateService,
  TableStateStorage,
  TableStateResult,
  TableStateInstance
} from 'src/app/modules/core/components/ag-grid/table-state-service';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { TranslateService } from '@ngx-translate/core';
import { ExportTemplate } from 'src/app/modules/management/store/datasets/dataset.model';
import { ActionMenuComponent } from '../action-menu/action-menu.component';

@Component({
  selector: 'topo-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
  standalone: false
})
export class DataTableComponent implements OnInit, OnDestroy {
  @ViewChild('tableContainer') tableContainer: ElementRef;
  @ViewChild('dragHandleTop') dragHandleTop: ElementRef;
  @ViewChild('dragHandleCorner') dragHandleCorner: ElementRef;
  @ViewChild('menu') actionsMenu: ActionMenuComponent;

  @Input() set isActive(value: boolean) {
    if (!value) {
      this._columns = [];
      this._rowData = [];

      this.hideSearch();
    }
    this._isActive = value;
  }
  get isActive(): boolean {
    return this._isActive;
  }
  _isActive = false;

  private tableState: TableStateInstance;

  @Input() isTableLoading: boolean;
  @Input() set leftDrawerOpen(value: boolean) {
    this._leftDrawerOpen = value;
    this.leftDrawerOpenClass = this._leftDrawerOpen ? 'is-left-drawer-open' : '';
  }
  get leftDrawerOpen(): boolean {
    return this._leftDrawerOpen;
  }
  private _leftDrawerOpen = false;

  @Input() rightDrawerOpen: boolean;
  @Input() htmlColorCode = '#333333';
  @Input() headerLabel: string = '';
  @Input() dataLabel: string = '';
  @Input() headerIcon: string = '';
  @Input() showFilter = true;
  @Input() isFloating = false;
  @Input() defaultHeight = 360;
  @Input() height?: number = null;
  @Input() width?: number = null;
  @Input() embedded: boolean;
  @Input() singleDatasetMode: boolean;
  @Input() tableHidden: boolean = false;
  @Input() hidePageLinks: boolean;
  @Input() tabs?: {
    uId: string;
    label: string;
    iconSettings: any;
  }[];
  @Input() tableType?: string;
  @Input() getRowId: (params: GetRowIdParams) => any = params => params?.data?.uid ?? params?.data?.uId;
  @Input() getRowClass: (params: RowClassParams) => any = null;
  _columns: any[];
  @Input() set columns(value: any) {
    this._columns = value;
    this.gridApi?.setGridOption('columnDefs', []); // ag-grid bug - force columns refresh
    this.gridApi?.setGridOption('columnDefs', this._columns);
    if (!!this.tableState) {
      this.tableState.initialColumns = this._columns;
    }
  }

  get columns(): any[] {
    return this._columns;
  }

  @Input() datasets?: Dataset[];
  @Input() datasetTemplates?: ExportTemplate[];
  _rowData = [];
  @Input() set rowData(value: any[]) {
    this._rowData = value;
    if (this.showLoading) {
      if (value === null) {
        this.doShowLoading(this.gridApi);
        this.suppressNoRowsOverlay = true;
      } else {
        if (value.length === 0) {
          this.suppressNoRowsOverlay = false;
          this.gridApi?.showNoRowsOverlay();
        }
      }
    }
  }
  get rowData(): any[] {
    return this._rowData;
  }
  @Input() documentsByLayer?: { layerUId: string; documents: Document[] }[];
  @Input() skip = 0;
  @Input() take = 25;
  @Input() defaultPageSize = 25;
  @Input() totalCount = 0;
  @Input() showHeader: boolean;
  @Input() showSearch: boolean;
  @Input() showMenu: boolean;
  @Input() autoHide: boolean = false;
  @Input() showLoading: boolean = false;
  @Input() showLoadingTimeout: number = 2500;
  @Input() steps = 2;
  @Input() maxPagesForShowAll = 10;
  @Input() showNewButton = false;
  @Input() showRefreshButtonCount = 0;
  @Input() newLabel = null;
  @Input() generateColumns: (columnNames: string[], datasets?: Dataset[]) => any[] = null;
  @Input() showSystemFields: boolean = false;
  @Input() selectedTab: string;
  @Input() useLocalExportToExcel: boolean;
  @Output() hide = new EventEmitter<void>();
  @Output() selectDocuments = new EventEmitter<{ layerDocuments: { layerUId: string; documents: Document[] }[] }>();
  @Output() zoomToDocument = new EventEmitter<{ layerDocuments: { layerUId: string; documents: Document[] }[] }>();
  @Output() takeChanged = new EventEmitter<number>();
  @Output() tabClick = new EventEmitter<string>();
  @Output() paginate = new EventEmitter<number>();
  @Output() refreshClick = new EventEmitter<void>();
  @Output() newClick = new EventEmitter<void>();
  @Output() sort = new EventEmitter<SortEvent>();
  @Output() exportToDocument = new EventEmitter<ExportToDocumentEvent>();
  @Output() exportToExcel = new EventEmitter<string[]>();

  get stateKey(): string {
    return this._stateKey;
  }

  @Input() set stateKey(key: string) {
    if (this._stateKey !== key) {
      this._stateKey = key;
      if (!this._stateStorage) {
        this._stateStorage = TableStateStorage.db;
      }
      this.tableState = this.tableStateService.init(
        this._stateKey,
        this.defaultPageSize,
        this.columns,
        this.columnsGenerator,
        this._stateStorage
      );
    }
  }

  private focusedId: string;

  // sets this id to be used for focused row
  // note: tableApi.getRowNode assumes that getRowId returns the correct id
  public setFocusedId(focusId?: string) {
    const tableApi = this.gridApi;
    const nodes = [];
    if (!!tableApi) {
      if (!!this.focusedId) {
        const prevNode = tableApi.getRowNode(this.focusedId);
        if (!!prevNode) {
          nodes.push(prevNode);
        }
      }
      if (!!focusId) {
        const newNode = tableApi.getRowNode(focusId);
        if (!!newNode) {
          nodes.push(newNode);
        }
      }
    }
    this.focusedId = focusId;
    tableApi?.redrawRows({ rowNodes: nodes });
  }

  getRowClassInternal = (params: RowClassParams): string | undefined => {
    if (!!this.getRowClass) {
      return this.getRowClass(params);
    }
    return this.focusedId === params.data?.uid ? 'ag-focused-row' : null;
  };

  get stateStorage(): TableStateStorage {
    return this._stateStorage;
  }

  getApi() {
    return this.gridApi;
  }

  @Input() set stateStorage(value: TableStateStorage) {
    if (this._stateStorage !== value) {
      this._stateStorage = value;
      if (!!this._stateKey) {
        this.tableState = this.tableStateService.init(
          this._stateKey,
          this.defaultPageSize,
          this.columns,
          this.columnsGenerator,
          this._stateStorage
        );
      }
    }
  }

  frameworkComponents = {
    dateTimeCellComponent: DateTimeCellComponent,
    geometryCellComponent: GeometryCellComponent,
    textCellComponent: TextCellComponent,
    autoIncrementCellComponent: AutoIncrementCellComponent,
    userCellComponent: UserCellComponent,
    numberCellComponent: NumberCellComponent,
    urlCellComponent: UrlCellComponent,
    picklistCellComponent: PicklistCellComponent,
    multiPicklistCellComponent: MultiPicklistCellComponent,
    imageCellComponent: ImageCellComponent,
    workflowCellComponent: WorkflowCellComponent,
    booleanCellComponent: BooleanCellComponent,
    colorCellComponent: ColorCellComponent,
    distanceCellComponent: DistanceCellComponent,
    linkCellComponent: LinkCellComponent,
    actionCellComponent: ActionCellComponent,
    cultureCodeCellComponent: CultureCodeCellComponent,
    durationCellComponent: DurationCellComponent,
    enumerationCellComponent: EnumerationCellComponent,
    businessUnitsCellComponent: BusinessUnitsCellComponent,
    referenceCellComponent: ReferenceCellComponent
  };

  rowSelection: RowSelectionOptions = {
    mode: 'multiRow',
    groupSelects: 'filteredDescendants',
    enableClickSelection: false,
    checkboxes: false,
    headerCheckbox: false
  };

  form: FormGroup;
  dragPosition = null;
  leftDrawerOpenClass = '';
  searchVisible = false;
  selectedCount: number = 0;
  title = 'my-app';
  sortHasBeenSet = false;
  showRowGroup = false;
  _stateKey: string;
  _stateStorage: TableStateStorage;
  suppressNoRowsOverlay: boolean = true;
  externalSort: boolean;
  externalGroup: boolean;
  hidden: boolean = false;
  showEditColumns = false;
  tableColumns: any[];
  id: string;
  defaultColDef: ColDef = { // TODO: maybe revert this?
    sortable: true,
    suppressHeaderContextMenu: true,
    suppressHeaderMenuButton: true,
    suppressHeaderFilterButton: true
  };

  groupRowRendererParams = {
    checkbox: true,
    rowDrag: true
  };

  private destroy$: Subject<void> = new Subject();
  private gridApi: GridApi;

  constructor(
    private fb: FormBuilder,
    private tableStateService: TableStateService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      filter: ['']
    });

    this.id = 'a' + uuidv4();

    this.form
      .get('filter')
      .valueChanges.pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe(val => {
        if (this._isActive) {
          this.gridApi?.setGridOption('quickFilterText', val);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  gridReady(params: AgGridEvent<any>): void {
    this.gridApi = params.api;

    if (this.showLoading) {
      this.suppressNoRowsOverlay = true;
      this.doShowLoading(this.gridApi);
    }

    if (this._stateKey) {
      this.tableState
        ?.applyTableState(params.api)
        .pipe(take(1))
        .subscribe(result => {
          this.showRowGroup = false;
          if (!!result) {
            this.externalSort = result.externalSort;
            this.externalGroup = result.externalGroup;
            this.take = result.take;
            this.skip = result.skip;
            this.showRowGroup = result.hasGroups === true;
            this.groupRows(this.showRowGroup);
          }
        });
    } else {
      this.groupRows(false);
    }
  }

  tableHeightCalc() {
    if (!!this.height) {
      return `${this.height}`;
    } else {
      if (this.showHeader) {
        return this.tabs?.length > 0 ? 'calc(100vh - 288px)' : 'calc(100vh - 238px)';
      } else {
        return this.tabs?.length > 0 ? 'calc(100vh - 200px)' : 'calc(100vh - 150px)';
      }
    }
  }

  rowDataChanged(params: AgGridEvent<any>): void {
    var result: TableStateResult;

    // default sort ascending by the first column in the table
    if (!this.sortHasBeenSet) {
      if (this.columns?.length > 0 && this.gridApi) {
        const column = this.columns[0];

        this.gridApi?.applyColumnState({
          state: [{ colId: column.field, sort: 'asc', sortIndex: 0 }],
          defaultState: { sort: null }
        });
      }
      this.sortHasBeenSet = true;
    }

    if (this.showLoading) {
      if (this._rowData === null) {
        this.doShowLoading(params.api);
      }
    }

    if (this._stateKey) {
      this.tableState
        ?.applyTableState(params.api)
        .pipe(take(1))
        .subscribe(result => {
          this.showRowGroup = false;
          if (!!result) {
            this.externalSort = result.externalSort;
            this.externalGroup = result.externalGroup;
            this.take = result.take;
            this.skip = result.skip;
            this.showRowGroup = result.hasGroups === true;
            this.groupRows(this.showRowGroup);
            this.selectedCount = 0;
          }
        });
    } else {
      this.groupRows(false);
    }
  }

  hideActions() {
    const cellRendererInstances = this.gridApi?.getCellRendererInstances();
    cellRendererInstances
      .map(c => {
        var cellRenderer: any = c;
        try {
          return cellRenderer.getFrameworkComponentInstance();
        } catch (e) {
          return null;
        }
      })
      .filter(r => {
        return r !== null && r instanceof ActionCellComponent;
      })
      .map(r => {
        r.hideActions();
      });
  }

  hideTable() {
    this.hide.emit();
  }

  exportLocalToExcel() {
    const rows = [];

    setTimeout(() => {
      this.actionsMenu.hideMenu();
    }, 100);

    // add the header row
    const headerRow = [];
    const columnSettings = [];
    const columns = this.gridApi?.getAllDisplayedColumns()?.filter(c => c.getColId() !== '_selectionChecks' && c.getColId() !== '_actions');

    for (const column of columns) {
      headerRow.push(column.getDefinition().headerName);

      columnSettings.push({
        wpx: 175
      });
    }
    rows.push(headerRow);

    // add the data rows
    this.gridApi?.forEachNodeAfterFilter((node, index) => {
      const rowData = [];
      for (const column of columns) {
        let value = node.getDataValue(column);

        if (moment.isMoment(value)) {
          value = value.format();
        } else {
          value = convert(value, {
            wordwrap: 130
          });
        }

        if (value.length > 32767) {
          value = value.substring(0, 32767);
        }
        rowData.push(value);
      }
      rows.push(rowData);
    });

    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = columnSettings;

    /* generate workbook and add the worksheet */
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

    const fileName = this.dataLabel?.length ? this.dataLabel : this.headerLabel?.length ? this.headerLabel : 'export';

    /* save to file */
    if (this.tableType === 'closest-asset' && this.tabs?.length > 0) {
      const selectedTabLabel = this.tabs.find(t => t.uId === this.selectedTab)?.label;
      XLSX.writeFile(wb, `${selectedTabLabel ? selectedTabLabel : fileName}.xlsx`);
    } else {
      XLSX.writeFile(wb, `${fileName}.xlsx`);
    }
  }

  exportToExcelClick() {
    setTimeout(() => {
      this.actionsMenu.hideMenu();
    }, 100);

    if (this.useLocalExportToExcel === true) {
      this.exportLocalToExcel();
    } else {
      const columns = this.gridApi
        ?.getColumnState()
        ?.filter(c => c.colId !== '_selectionChecks' && c.colId !== '_actions')
        .map(c => c.colId);
      this.exportToExcel.emit(columns);
    }
  }

  exportToDocumentClicked(template: ExportTemplate, type: ExportToDocumentType) {
    if (this.exportToDocument) {
      const documents = [];

      this.gridApi?.forEachNodeAfterFilter((node, index) => {
        documents.push(node.data);
      });

      this.exportToDocument.emit({ template: template, documents: documents, type: type });

      setTimeout(() => {
        this.actionsMenu.hideMenu();
      }, 100);
    }
  }

  editColumns() {
    this.tableColumns = this.gridApi?.getColumnDefs();
    this.showEditColumns = true;
  }

  closeEditColumns() {
    this.showEditColumns = false;
  }

  columnsGenerator = (columnNames: string[]) => {
    if (!!this.datasets && this.generateColumns) {
      return this.generateColumns(columnNames, this.datasets);
    } else {
      return null;
    }
  };

  saveEditColumns(columnNames: string[]) {
    this.showEditColumns = false;
    const results = this.generateColumns ? this.generateColumns(columnNames, this.datasets) : null;
    if (!!results && !!this.gridApi) {
      this.gridApi.setGridOption('columnDefs', []);
      this.gridApi.setGridOption('columnDefs', results);
      this.tableState?.saveColumnsState(this.gridApi);
    }
  }

  resetEditColumns(): void {
    this.showEditColumns = false;
    this.gridApi?.setGridOption('columnDefs', []);
    this.gridApi?.setGridOption('columnDefs', this.columns);
    this.tableState?.saveColumnsState(this.gridApi);
  }

  resetState() {
    this.tableState?.resetState(this.gridApi);

    this.tableState
      ?.applyTableState(this.gridApi)
      .pipe(take(1))
      .subscribe(result => {
        this.showRowGroup = false;
        if (!!result) {
          this.externalSort = result.externalSort;
          this.externalGroup = result.externalGroup;
          this.take = result.take;
          this.skip = result.skip;
          this.showRowGroup = result.hasGroups === true;
          this.groupRows(this.showRowGroup);
        }
      });
  }

  groupRows(value?: boolean) {
    if (value !== undefined) {
      this.showRowGroup = value === true;
    } else {
      this.showRowGroup = !this.showRowGroup;
    }

    if (this.selectedCount > 0) {
      this.showRowGroup = false;
    }
    let groupElement = document.querySelector(`#${this.id} .ag-column-drop-wrapper`) as HTMLElement;
    groupElement.style.display = this.showRowGroup ? 'block' : 'none';
  }

  selectionChanged($event) {
    this.groupRows(false);

    const nodes = this.gridApi?.getSelectedNodes();

    const layerDocuments = [] as { layerUId: string; documents: Document[] }[];

    this.selectedCount = nodes?.length ?? 0;

    if (!!nodes && nodes.length > 0) {
      const documents = nodes.map(n => n.data);

      if (this.singleDatasetMode) {
        const layerDocs = { layerUId: null, documents: [] };
        layerDocuments.push(layerDocs);
        documents.forEach(document => {
          layerDocs.documents.push(document);
        });
      } else {
        documents.forEach(document => {
          const docUId = new DocumentReader(document).uId;
          // The same document could be in the table once for each layer it is in,
          //  but for now, just look for the first instance of this document
          const layerUId = this.documentsByLayer?.find(l => l.documents.find(d => new DocumentReader(d).uId === docUId))?.layerUId;
          let ld = layerDocuments.find(ld => ld.layerUId === layerUId);
          if (!ld) {
            layerDocuments.push({ layerUId, documents: [document] });
          } else {
            ld.documents.push(document);
          }
        });
      }
    }

    this.selectDocuments.emit({ layerDocuments });
  }

  rowDoubleClicked($event) {
    if (!this.singleDatasetMode) {
      const document = $event.data;
      const docUId = new DocumentReader(document).uId;
      // The same document could be in the table once for each layer it is in,
      //  but for now, just look for the first instance of this document
      const layerUId = this.documentsByLayer?.find(l => l.documents.find(d => new DocumentReader(d).uId === docUId))?.layerUId;
      this.zoomToDocument.emit({ layerDocuments: [{ layerUId, documents: [document] }] });
    }
  }

  tabClicked(uId: string) {
    this.gridApi.deselectAll();
    this.tabClick.emit(uId);
  }

  onPaginate(skip: number) {
    this.gridApi?.paginationGoToPage(skip / this.take);
    this.tableState?.savePagingState(this.gridApi);
    this.skip = skip;
    this.hidden = false;
    this.paginate.emit(skip);
  }

  onTakeChanged(takeNumber: number) {
    this.gridApi?.setGridOption('paginationPageSize', takeNumber);
    this.tableState?.savePagingState(this.gridApi);
    this.take = takeNumber;
    this.hidden = false;
    this.takeChanged.emit(takeNumber);
  }

  onNewClick(): void {
    this.newClick.emit();
  }

  onRefreshClick(): void {
    this.refreshClick.emit();
  }

  private doShowLoading(api: GridApi): void {
    api?.setGridOption('loading', true);
    setTimeout(() => {
      api?.setGridOption('loading', false);
      if (this._rowData === null || this._rowData?.length === 0) {
        this.suppressNoRowsOverlay = false;
        this.gridApi?.showNoRowsOverlay();
      }
    }, this.showLoadingTimeout);
  }

  onShowSearch(): void {
    this.searchVisible = true;

    const searchFields = document.getElementsByClassName('search-text-field');
    if (searchFields?.length > 0) {
      const search = searchFields[0] as HTMLInputElement;
      search.focus();
    }
  }

  paginationChanged(event: PaginationChangedEvent) {
    const page = event.api.paginationGetCurrentPage();
    this.skip = page * this.take;
    this.totalCount = event.api.getDisplayedRowCount();
  }

  hideSearch(): void {
    this.gridApi?.resetQuickFilter();
    this.form?.get('filter')?.reset();
    this.searchVisible = false;
  }

  columnResized(event: any): void {
    this.tableState?.saveColumnsState(this.gridApi);
  }

  dragStopped(event: any): void {
    this.tableState?.saveColumnsState(this.gridApi);
  }

  groupChanged(event: any): void {
    if (!this.externalGroup && event.source !== 'api' && event.source !== 'gridOptionsChanged') {
      this.tableState?.saveColumnsState(this.gridApi);
    }
    this.externalGroup = false;
  }

  getLocaleText = (params: GetLocaleTextParams): string => {
    return this.translateService.instant(`AG-GRID.${params.key}`);
  };

  sortChanged(event: any): void {
    const sortedColumnStates = this.gridApi?.getColumnState()?.filter(column => !!column.sort);
    const sortedColumnState = sortedColumnStates.length > 0 ? sortedColumnStates[0] : null;

    if (!this.externalSort && event.source !== 'api') {
      this.tableState?.saveColumnsState(this.gridApi);
    } else {
      this.tableState
        ?.applyPaging(this.gridApi)
        .pipe(take(1))
        .subscribe(result => {
          if (result) {
            this.take = result.take;
            this.skip = result.skip;
          }
        });
    }

    this.externalSort = false;

    if (!sortedColumnState) {
      this.sort.emit(null);
      return;
    }

    const sortedColumn = this.gridApi?.getColumn(sortedColumnState.colId);
    if (!sortedColumn) {
      this.sort.emit(null);
      return;
    }

    const colDef = this.gridApi?.getColumnDef(sortedColumn);
    if (!colDef) {
      this.sort.emit(null);
      return;
    }

    var systemField = colDef.field;

    this.sort.emit({
      sortField: systemField,
      sortDirection: sortedColumnState.sort
    });
  }
}

export interface SortEvent {
  sortField: string;
  sortDirection: string;
}

export interface PersistOptions {
  stateKey?: string;
  storage?: TableStateStorage;
}

export interface ExportToDocumentEvent {
  template?: ExportTemplate;
  documents?: Document[];
  type?: ExportToDocumentType;
}

export enum ExportToDocumentType {
  word = 1,
  pdf = 2
}
